import React, { useState, useRef, useCallback, useMemo, lazy, Suspense } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { buildReportPayload } from '../../services/payloads/reportPayload';
import { fetchApi } from '../../services/api';
import { getCurrentUserEmail } from '../../services/auth';
import {
  TextField,
  Button,
  Checkbox,
  IconButton,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Autocomplete,
  Select,
  MenuItem,
  FormControlLabel,
  Chip,
  InputAdornment,
  Popover,
  FormControl,
  InputLabel,
  Alert,
  Typography
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "../../assets/images/edit.svg";
import DeleteIcon from "../../assets/images/dustbin.svg";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import { sanitizeDataArray } from '../../utils/csvSanitizer';
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import Header from "../../sharedcomponents/header/header";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format as formatDate, subMonths, addMonths, startOfMonth, endOfMonth, startOfDay, endOfDay } from "date-fns";
import Skeleton from "@mui/material/Skeleton";
import { getDivisionList, setDivisionList } from '../../services/employeeStore';
import DeleteConfirmation from "../../sharedcomponents/delete-confirmation/DeleteConfirmation";
import "./report.css";

const DateCalendar = lazy(() => import("@mui/x-date-pickers/DateCalendar").then(m => ({ default: m.DateCalendar })));

// Module-level cache so revisiting the page shows data instantly
let _reportCache = { key: '', data: [] };



/* ----------------------------- Helpers ----------------------------- */



const applyDateFilter = (rows, start, end) => {
  const normalizedStart = start ? startOfDay(start) : null;
  const normalizedEnd = end ? endOfDay(end) : null;
  if (!normalizedStart && !normalizedEnd) return rows;
  return rows.filter(r => {
    const dates = [r.complainReportingDate, r.startDate]
      .filter(Boolean)
      .map(d => startOfDay(new Date(d)));
    if (dates.length === 0) return false;
    return dates.every(d => {
      if (normalizedStart && d < normalizedStart) return false;
      if (normalizedEnd && d > normalizedEnd) return false;
      return true;
    });
  });
};

const sanitizeAlphaNumeric = (value) => {
  const str = value || "";
  const maxLen = 500;
  return str.slice(0, maxLen).replace(/[^a-zA-Z0-9@. \-_]/g, "");
};

const useDebounce = (delay = 300) => {
  const timerRef = useRef(null);
  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  const run = useCallback(
    (fn) => {
      cancel();
      timerRef.current = setTimeout(fn, delay);
    },
    [delay, cancel]
  );
  React.useEffect(() => cancel, [cancel]);
  return { run, cancel };
};



/* --------------------------- Main Component --------------------------- */

function ReportsPage({ mode = "reports", statusFilter = null, reportType = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine page mode based on URL (matching old Angular logic)
  const pageMode = location.pathname.includes('eight-dforms') || location.pathname.includes('8D-forms') ? 'edit' : 'view';
  const actualMode = pageMode === 'edit' ? 'forms' : 'reports';
  const actualReportType = pageMode === 'edit' ? 'edit' : 'view';

  /* Edit row handler */
  const handleEditRow = async (eightDInfoId, reportNo) => {
    try {
      const headerResponse = await fetchApi('/api/EightDHeader/Get8DHeader', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ EightDInfoId: eightDInfoId }),
      });
      
      const d1Response = await fetchApi('/api/D1/GetD1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) }),
      });
      
      const sortedD1Data = d1Response?.data ? 
        [...d1Response.data].sort((a, b) => {
          if (a.MemberRole === 'Originator') return -1;
          if (b.MemberRole === 'Originator') return 1;
          return 0;
        }) : [];
      
      const fetchDData = async (endpoint) => {
        try {
          const response = await fetchApi(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) }),
          });
          return response?.data || null;
        } catch (error) {
          return null;
        }
      };
      
      const [d2Data, d2ResponsibleData, d3Data, d3ResponsibleData, d4Data, d4ResponsibleData, d5Data, d5ResponsibleData, d6Data, d6ResponsibleData, d7Data, d7ResponsibleData, d8Data] = await Promise.all([
        fetchDData('/api/D2/GetD2'),
        fetchDData('/api/D2/GetD2Responsible'),
        fetchDData('/api/D3/GetD3'),
        fetchDData('/api/D3/GetD3Responsible'),
        fetchDData('/api/D4/GetD4'),
        fetchDData('/api/D4/GetD4Responsible'),
        fetchDData('/api/D5/GetD5'),
        fetchDData('/api/D5/GetD5Responsible'),
        fetchDData('/api/D6/GetD6'),
        fetchDData('/api/D6/GetD6Responsible'),
        fetchDData('/api/D7/GetD7'),
        fetchDData('/api/D7/GetD7Responsible'),
        fetchDData('/api/D8/GetD8')
      ]);
      
      navigate('/create-new', { 
        state: { 
          reportData: headerResponse?.data, 
          d1Data: sortedD1Data,
          d2Data,
          d2ResponsibleData,
          d3Data,
          d3ResponsibleData,
          d4Data,
          d4ResponsibleData,
          d5Data,
          d5ResponsibleData,

          d6Data,
          d6ResponsibleData,
          d7Data,
          d7ResponsibleData,
          d8Data,
          eightDInfoId: Number(eightDInfoId),
          mode: 'edit',
          currentUserEmail: getCurrentUserEmail()
        } 
      });
    } catch (error) {
      navigate('/create-new');
    }
  };

  /* Top-bar search & chips */
  const [focusState, setFocusState] = useState(false);
  const [chips, setChips] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [filteredData, setFilteredData] = useState([]);


  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteAlert, setDeleteAlert] = useState(null);

  /* Date range + popover anchor for icon click */
  const [dateStart, setDateStart] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(subMonths(new Date(), 1));
  const isCalendarsOpen = Boolean(calendarAnchorEl);
  const openCalendars = (event) => { setCalendarAnchorEl(event.currentTarget); setCalendarMonth(subMonths(new Date(), 1)); };
  const closeCalendars = () => setCalendarAnchorEl(null);

  /* Filter parameters */



  // Auto-close when both dates are selected (optional - can be disabled for better UX)
  React.useEffect(() => {
    // Commented out auto-close to give users more control
    // if (dateStart && dateEnd && isCalendarsOpen) {
    //   closeCalendars();
    // }
  }, [dateStart, dateEnd, isCalendarsOpen]);


  const searchInputRef = useRef(null);
  const [, setSearchAnchorEl] = useState(null);

  const openFilterPanelFromSearch = (e) => {
    setFocusState(true);
    setSearchAnchorEl(searchInputRef.current);
  };

  const closeFilterPanel = () => {
    setFocusState(false);
    setSearchAnchorEl(null);
  };


  const [searchParameter, setSearchParameter] = useState({
    reportNo: "",
    supplierName: "",
    partNo: "",
    division: "",
    originatorEmailID: "",
    coordinatorEmailID: "",
    responsibleEmailID: "",
    isOverdue: false,
    problemStatement: ""
  });

  /* Debounce helper for email lookups */
  const { run: debounceRun, cancel: cancelDebounce } = useDebounce(300);

  /* Sample lists */
  const [Divisions, setDivisions] = useState([]);

  // Load divisions from store or fetch from API
  React.useEffect(() => {
    const stored = getDivisionList();
    if (stored && stored.length > 0) {
      setDivisions(stored.map(d => d.division || d.Division || d).filter(Boolean));
      return;
    }
    // Defer division fetch so it doesn't block initial data load
    const timer = setTimeout(() => {
      fetchApi('/api/Dashboard/GetDivision', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ EightDInfoId: 0, Division: '', Type: 'CLOSURE', pageSize: 0, pageNumber: 0 }),
        skipLoader: true,
      }).then(data => {
        const list = data?.data || [];
        setDivisionList(list);
        setDivisions(list.map(d => d.division || d.Division || d).filter(Boolean));
      }).catch(() => {});
    }, 500);
    return () => clearTimeout(timer);
  }, []);
  const originatorEmailsList = [{ emailId: "originator@example.com" }];
  const coordinatorEmailsList = [{ emailId: "coordinator@example.com" }];
  const responsibleEmailsList = [{ emailId: "responsible@example.com" }];

  /* Table data - filtered to show only Drafted status */
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load all data once on mount
  React.useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Selection & pagination */
  const [selected, setSelected] = useState([]);
  const isSelected = (id) => selected.includes(id);
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(displayData.map((n) => n.eightDInfoId));
    } else {
      setSelected([]);
    }
  };
  const handleRowSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const handleChangePage = (_evt, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (evt) => {
    setRowsPerPage(parseInt(evt.target.value, 10));
    setPage(0);
  };

  /* Chips */
  const separatorKeys = ["Enter", ","];
  const addChipFromInput = () => {
    const name = (searchInput || "").trim();
    if (!name) return;
    const newChips = [...chips, { name }];
    setChips(newChips);
    setSearchInput("");
    setPage(0);
    setFilteredData(filterAndSort(data, "", newChips));
  };
  const removeChip = (idx) => {
    const newChips = chips.filter((_, i) => i !== idx);
    setChips(newChips);
    setPage(0);
    setFilteredData(filterAndSort(data, searchInput, newChips));
  };

  const searchFieldValues = (row) => [
    row.reportNo, row.problemStatement, row.partNo,
    row.partDescription, row.supplierName, row.sourceOfComplain, row.status
  ];

  const getMatchScore = (row, term) => {
    if (row.reportNo?.toLowerCase() === term) return 4;
    if (row.partNo?.toLowerCase() === term) return 3;
    if (row.reportNo?.toLowerCase().includes(term)) return 2;
    return 1;
  };

  const filterAndSort = (baseData, liveText, chipList) => {
    const terms = [
      ...(liveText?.trim() ? [liveText.trim().toLowerCase()] : []),
      ...chipList.map(c => c.name.toLowerCase())
    ];
    if (terms.length === 0) return baseData;

    return baseData
      .filter(row => terms.every(term =>
        searchFieldValues(row).some(f => f?.toLowerCase().includes(term))
      ))
      .map(row => ({ row, score: terms.reduce((s, t) => s + getMatchScore(row, t), 0) }))
      .sort((a, b) => b.score - a.score)
      .map(({ row }) => row);
  };

  const handleSearchInputChange = (e) => {
    const val = sanitizeAlphaNumeric(e.target.value);
    setSearchInput(val);
    setPage(0);
    setFilteredData(filterAndSort(data, val, chips));
  };

  const applyAdvancedFilter = (dataToFilter) => {
    let filtered = [...dataToFilter];
    
    if (searchParameter.reportNo) {
      filtered = filtered.filter(row => 
        row.reportNo?.toLowerCase().includes(searchParameter.reportNo.toLowerCase())
      );
    }
    if (searchParameter.supplierName) {
      filtered = filtered.filter(row => 
        row.supplierName?.toLowerCase().includes(searchParameter.supplierName.toLowerCase())
      );
    }
    if (searchParameter.partNo) {
      filtered = filtered.filter(row => 
        row.partNo?.toLowerCase().includes(searchParameter.partNo.toLowerCase())
      );
    }
    if (searchParameter.division) {
      filtered = filtered.filter(row => 
        row.division?.toLowerCase().includes(searchParameter.division.toLowerCase())
      );
    }
    if (searchParameter.problemStatement) {
      filtered = filtered.filter(row => 
        row.problemStatement?.toLowerCase().includes(searchParameter.problemStatement.toLowerCase())
      );
    }
    if (searchParameter.originatorEmailID) {
      filtered = filtered.filter(row =>
        row.originatorEmailID?.toLowerCase().includes(searchParameter.originatorEmailID.toLowerCase())
      );
    }
    if (searchParameter.coordinatorEmailID) {
      filtered = filtered.filter(row =>
        row.coordinatorEmailID?.toLowerCase().includes(searchParameter.coordinatorEmailID.toLowerCase())
      );
    }
    if (searchParameter.responsibleEmailID) {
      filtered = filtered.filter(row =>
        row.responsibleEmailID?.toLowerCase().includes(searchParameter.responsibleEmailID.toLowerCase())
      );
    }
    if (dateStart || dateEnd) {
      const normalizedStart = dateStart ? startOfDay(dateStart) : null;
      const normalizedEnd = dateEnd ? endOfDay(dateEnd) : null;
      filtered = filtered.filter(row => {
        const dates = [
          row.complainReportingDate,
          row.startDate
        ].filter(Boolean).map(d => startOfDay(new Date(d)));
        if (dates.length === 0) return false;
        return dates.every(d => {
          if (normalizedStart && d < normalizedStart) return false;
          if (normalizedEnd && d > normalizedEnd) return false;
          return true;
        });
      });
    }
    
    return filtered;
  };

  /* Inputs */
  const handleParamChange = (field) => (e) => {
    const val = sanitizeAlphaNumeric(e.target.value);
    setSearchParameter((prev) => ({ ...prev, [field]: val }));
  };
  const handleEmailTyping =
    (sourceKey) =>
      (_event, value /* reason */) => {
        const cleanVal = sanitizeAlphaNumeric(value);
        setSearchParameter((prev) => ({ ...prev, [sourceKey]: cleanVal }));
        debounceRun(() => {
        });
      };

  /* Date helpers */
  const formatDisplayRange = () => {
    const fmt = "dd-MM-yyyy";
    if (dateStart && dateEnd)
      return `${formatDate(dateStart, fmt)} to ${formatDate(dateEnd, fmt)}`;
    if (dateStart && !dateEnd) return `${formatDate(dateStart, fmt)} to …`;
    if (!dateStart && dateEnd) return `… to ${formatDate(dateEnd, fmt)}`;
    return "";
  };

  const processRawData = (raw) => {
    const statusLower = statusFilter?.toLowerCase();
    const modeLower = actualMode;
    const mappedData = [];
    for (let i = 0; i < raw.length; i++) {
      const item = raw[i];
      const s = item.Status?.toLowerCase();
      
      // Skip if statusFilter is set and status doesn't match
      if (statusLower && s !== statusLower) continue;
      
      // For 8D Forms page: exclude closed reports
      if (modeLower === 'forms' && s === 'closed') continue;
      
      // For Reports page: exclude drafted reports only
      if (modeLower === 'reports' && s === 'drafted') continue;
      
      mappedData.push({
        eightDInfoId: item.EightDInfoId,
        reportNo: item.ReportNo,
        complainReportingDate: item.ComplainReportingDate,
        problemStatement: item.ProblemStatement,
        partNo: item.PartNo,
        partDescription: item.PartDescription,
        sourceOfComplain: item.SourceOfComplain,
        supplierName: item.SupplierName,
        startDate: item.StartDate,
        endDate: item.EndDate,
        status: item.Status,
        division: item.Division || '',
        originatorEmailID: item.OriginatorEmailId || item.OriginatorEmailID || '',
        coordinatorEmailID: item.CoordinatorEmailId || item.CoordinatorEmailID || '',
        responsibleEmailID: item.ResponsibleEmailId || item.ResponsibleEmailID || ''
      });
    }
    mappedData.sort((a, b) => (parseInt(b.reportNo) || 0) - (parseInt(a.reportNo) || 0));
    return mappedData;
  };

  /* Load all data from API (called once on mount) */
  const loadData = async () => {
    // Show cached data instantly if available for this mode
    const cacheKey = actualReportType;
    if (_reportCache.key === cacheKey && _reportCache.data.length > 0) {
      setData(_reportCache.data);
      setFilteredData(applyDateFilter(_reportCache.data, dateStart, dateEnd));
    }

    setLoading(true);
    try {
      // B2C supplier: always filter by their email as responsible person
      const isSupplierUser = sessionStorage.getItem('user_type') === 'supplier';
      const supplierEmail = isSupplierUser ? getCurrentUserEmail() : '';

      const payload = buildReportPayload({
        type: actualReportType,
        pageSize: 0,
        pageNumber: 0,
        startDate: dateStart,
        endDate: dateEnd,
        reportNo: searchParameter.reportNo,
        problemStatement: searchParameter.problemStatement,
        partNo: searchParameter.partNo,
        supplierName: searchParameter.supplierName,
        division: searchParameter.division,
        originatorEmailID: searchParameter.originatorEmailID,
        coordinatorEmailID: searchParameter.coordinatorEmailID,
        responsibleEmailID: isSupplierUser ? supplierEmail : searchParameter.responsibleEmailID,
        isOverdue: searchParameter.isOverdue,
      });
      const response = await fetchApi('/api/Report/GetReport', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        skipLoader: true,
      });

      const mappedData = processRawData(response?.data || []);

      // Update cache
      _reportCache = { key: cacheKey, data: mappedData };

      setData(mappedData);
      
      // Apply client-side filters after server response
      const clientFiltered = applyAdvancedFilter(mappedData);
      const dateFiltered = applyDateFilter(clientFiltered, dateStart, dateEnd);
      setFilteredData(dateFiltered);
    } catch (error) {
      setData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  /* Apply advanced filter panel */
  const handleApply = async () => {
    closeFilterPanel();
    const needsServerFilter = searchParameter.originatorEmailID || searchParameter.coordinatorEmailID || searchParameter.responsibleEmailID || searchParameter.isOverdue;
    if (needsServerFilter) {
      await loadData();
    } else {
      const result = applyAdvancedFilter(data);
      const final = filterAndSort(result, searchInput, chips);
      setFilteredData(final);
      setPage(0);
    }
  };

  const handleReset = () => {
    setSearchParameter({
      reportNo: "",
      supplierName: "",
      partNo: "",
      division: "",
      originatorEmailID: "",
      coordinatorEmailID: "",
      responsibleEmailID: "",
      isOverdue: false,
      problemStatement: ""
    });
    setChips([]);
    setSearchInput("");
    setDateStart(null);
    setDateEnd(null);
    setPage(0);
    setFilteredData(data);
    cancelDebounce();
  };

  const mapEmails = (list) => list.map((e) => e.emailId);

  /* Excel export */
  const handleDownload = () => {
    const fmtDate = (v) => {
      if (!v) return "";
      const d = new Date(v);
      return isNaN(d.getTime()) ? "" : formatDate(d, "dd-MM-yyyy");
    };

    const wsData = data.map((d) => ({
      "Report No.": d.reportNo ?? "",
      "Complaint Reporting Date": fmtDate(d.complainReportingDate),
      "Problem Statement": d.problemStatement ?? "",
      "Part No.": d.partNo ?? "",
      "Part Description": d.partDescription ?? "",
      "Source of Complain": d.sourceOfComplain ?? "",
      "Supplier Name": d.supplierName ?? "",
      "Start Date": fmtDate(d.startDate),
      "End Date": fmtDate(d.endDate),
      "Status": d.status ?? ""
    }));

    // Sanitize data to prevent CSV formula injection
    const sanitizedData = sanitizeDataArray(wsData);

    const ws = XLSX.utils.json_to_sheet(sanitizedData);

    /* Auto-fit column widths */
    const colKeys = Object.keys(sanitizedData[0] || {});
    ws["!cols"] = colKeys.map((key) => {
      const maxLen = Math.max(key.length, ...sanitizedData.map((r) => String(r[key] || "").length));
      return { wch: Math.min(maxLen + 2, 40) };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "8D Reports");
    XLSX.writeFile(wb, `8D-reports-${formatDate(new Date(), "yyyyMMdd-HHmmss")}.xlsx`);
  };

  const onView = (row) => {
    navigate('/detail-reports', { state: { eightDInfoId: row.eightDInfoId } });
  };
  const onEdit = (row) => handleEditRow(row.eightDInfoId, row.reportNo);
  const onDelete = (row) => {
    setDeleteRow(row);
    setDeleteDialogOpen(true);
  };
  const handleDeleteConfirm = async (result) => {
    setDeleteDialogOpen(false);
    if (result !== 'confirm' || !deleteRow) return;
    try {
      await fetchApi('/api/Dashboard/DeleteEightDReport', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ EightDInfoId: deleteRow.eightDInfoId }),
      });
      const deletedId = deleteRow.eightDInfoId;
      setData(prev => prev.filter(r => r.eightDInfoId !== deletedId));
      setFilteredData(prev => prev.filter(r => r.eightDInfoId !== deletedId));
      setDeleteAlert('Record deleted successfully');
      setTimeout(() => setDeleteAlert(null), 3000);
    } catch (error) {
      setDeleteAlert('Failed to delete record');
      setTimeout(() => setDeleteAlert(null), 3000);
    }
    setDeleteRow(null);
  };

  const setClass = (status) => {
    if (status === "Closed") return "closed";
    if (status === "Pending Form Completion") return "pending";
    if (status === "Report Closure") return "reportClosue";
    return "";
  };

  const TOP_BAR_HEIGHT = 56; // equal height for both inputs






  /* 2) DERIVED VALUES (MUST come AFTER the state above) ---- */
  const displayData = filteredData;
  const start = page * rowsPerPage;
  const end = start + rowsPerPage;
  const pageRows = useMemo(() => displayData.slice(start, end), [displayData, start, end]);
  const totalPages = Math.max(1, Math.ceil(displayData.length / rowsPerPage));

  const allOnPageSelected =
    pageRows.length > 0 && pageRows.every(r => selected.includes(r.eightDInfoId));

  const someOnPageSelected =
    selected.length > 0 &&
    !allOnPageSelected &&
    pageRows.some(r => selected.includes(r.eightDInfoId));

  const onToggleAll = () => {
    const idsOnPage = pageRows.map(r => r.eightDInfoId);
    setSelected(prev =>
      allOnPageSelected
        ? prev.filter(id => !idsOnPage.includes(id))             // deselect current page
        : Array.from(new Set([...prev, ...idsOnPage]))           // select current page
    );
  };

  const selectedIds = selected;
  const onToggleRow = handleRowSelect;

  const onPageChange = (newPage) => setPage(newPage);
  const onRowsPerPageChange = (newRpp) => {
    setRowsPerPage(Number(newRpp));
    setPage(0);
  };

  const fmt = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit", month: "2-digit", year: "numeric"
    }).format(d);
  };


  return (
    <Header>
      <div className="contentSection col-12 pl-0 pr-0">
        <form>
          <div className="mainContent pl-0 pr-0">
            <div className="reports-container">
              {/* Page Title */}
              <div className="PageTitle">
                <span>All 8D Reports</span>
                <div className="addNew">
                  <IconButton
                    aria-label="Download reports as CSV"
                    onClick={handleDownload}
                  >
                    <DownloadIcon sx={{ color: "#fff", width: 24, height: 24 }} />
                  </IconButton>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="filters">
              {/* ONE LINE: Search + Date (underline only, icons inside, paddings applied) */}
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "stretch",
                    gap: 2,
                    flexWrap: "nowrap",
                    height: TOP_BAR_HEIGHT
                  }}
                >
                  {/* Search box (flex:1) with LEFT padding; underline only; placeholder font = GEG Body Copy */}
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "stretch",
                      pl: 2 // left padding
                    }}
                  >
                    <TextField
                      placeholder="Search or Filter"
                      value={searchInput}
                      onChange={handleSearchInputChange}
                      // onFocus={() => setFocusState(true)}
                      // onKeyDown={onSearchKeyDown}

                      onFocus={openFilterPanelFromSearch}
                      onClick={openFilterPanelFromSearch}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addChipFromInput();
                        }
                      }}
                      fullWidth
                      variant="standard"
                      inputRef={searchInputRef}
                      inputProps={{ "aria-label": "Search or Filter" }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="commit search term"
                              onClick={() => {
                                if (searchInput.trim()) {
                                  addChipFromInput();
                                } else {
                                  openFilterPanelFromSearch();
                                }
                              }}
                              size="small"
                            >
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        height: "100%",
                        "& .MuiInputBase-root": {
                          height: "100%",
                          alignItems: "center"
                        },
                        // underline colors use filters border color

                        "& .MuiInput-underline:before": {
                          borderBottom: "1px solid #F1F1ED",
                        },
                        "& .MuiInput-underline:hover:before": {
                          borderBottom: "1px solid #F1F1ED",
                        },
                        "& .MuiInput-underline:after": {
                          borderBottom: "1px solid #F1F1ED",
                        },

                        // placeholder font
                        "& .MuiInputBase-input::placeholder": {
                          fontFamily: '"GEG Body Copy","Segoe UI",Arial,sans-serif',
                          fontSize: "0.95rem",
                          opacity: 0.85
                        },
                        // vertically center text in taller input
                        "& .MuiInputBase-input": {
                          paddingTop: "14px",
                          paddingBottom: "14px"
                        }
                      }}
                    />
                  </Box>

                  {/* Date range display with RIGHT padding; underline only; placeholder font = GEG Body Copy */}
                  <Box
                    sx={{
                      width: { xs: 220, sm: 300, md: 360 },
                      display: "flex",
                      alignItems: "stretch",
                      pr: 2 // right padding
                    }}
                  >
                    <TextField
                      value={formatDisplayRange()}
                      placeholder="Date 01-02-18 to 30-02-18"
                      fullWidth
                      variant="standard"
                      inputProps={{ readOnly: true, "aria-label": "Selected date range" }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="Select date range"
                              onClick={openCalendars}
                              edge="end"
                              size="small"
                            >
                              <CalendarTodayIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        height: "100%",
                        "& .MuiInputBase-root": {
                          height: "100%",
                          alignItems: "center"
                        },

                        "& .MuiInput-underline:before": {
                          borderBottom: "1px solid #F1F1ED",
                        },
                        "& .MuiInput-underline:hover:before": {
                          borderBottom: "1px solid #F1F1ED",
                        },
                        "& .MuiInput-underline:after": {
                          borderBottom: "1px solid #F1F1ED",
                        },

                        "& .MuiInputBase-input": {
                          paddingTop: "14px",
                          paddingBottom: "14px",
                          fontFamily: '"GEG Body Copy", sans-serif',
                          fontSize: "0.95rem",
                          color: "#000000de"
                        },


                        "& .MuiInputBase-input::placeholder": {
                          fontFamily: '"GEG Body Copy", sans-serif',
                          fontSize: "0.95rem",
                          opacity: 0.85
                        }
                      }}
                    />
                  </Box>
                </Box>

                {/* TWO inline calendars in a popover (opens on calendar icon click) */}
                {/* <Popover
                  open={isCalendarsOpen}
                  anchorEl={calendarAnchorEl}
                  onClose={closeCalendars}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{ sx: { p: 2 } }}
                >
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "nowrap" }}>
                    <StaticDatePicker
                      displayStaticWrapperAs="desktop"
                      value={dateStart}
                      onChange={(newVal) => setDateStart(newVal)}
                      format="dd-MM-yyyy"
                      // inputFormat="dd-MM-yyyy" // for MUI X v5
                    />
                    <StaticDatePicker
                      displayStaticWrapperAs="desktop"
                      value={dateEnd}
                      onChange={(newVal) => setDateEnd(newVal)}
                      minDate={dateStart || undefined}
                      format="dd-MM-yyyy"
                      // inputFormat="dd-MM-yyyy" // for MUI X v5
                    />
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1 }}>
                    <Button onClick={() => { setDateStart(null); setDateEnd(null); }}>
                      Clear
                    </Button>
                    <Button variant="contained" onClick={closeCalendars}>
                      Done
                    </Button>
                  </Box>
                </Popover> */}

                <Popover
                  open={isCalendarsOpen}
                  anchorEl={calendarAnchorEl}
                  onClose={closeCalendars}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{ sx: { p: 2 } }}
                  disableAutoFocus
                  disableEnforceFocus
                >
                  <Suspense fallback={<Box sx={{ p: 4 }}>Loading...</Box>}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", flexWrap: "nowrap" }}>
                    <IconButton
                      onClick={() => setCalendarMonth(prev => subMonths(prev, 1))}
                      sx={{ mt: 4 }}
                    >
                      <Typography sx={{ fontSize: '1.2rem' }}>&lt;</Typography>
                    </IconButton>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Start Date</Typography>
                      <DateCalendar
                        key={`s-${calendarMonth.getFullYear()}-${calendarMonth.getMonth()}`}
                        value={dateStart && dateStart.getMonth() === calendarMonth.getMonth() && dateStart.getFullYear() === calendarMonth.getFullYear() ? dateStart : null}
                        referenceDate={startOfMonth(calendarMonth)}
                        onChange={(newVal) => {
                          setDateStart(newVal);
                          setPage(0);
                          const normalizedStart = newVal ? startOfDay(newVal) : null;
                          const normalizedEnd = dateEnd ? endOfDay(dateEnd) : null;
                          const filtered = data.filter(r => {
                            const dates = [r.complainReportingDate, r.startDate].filter(Boolean).map(d => startOfDay(new Date(d)));
                            if (dates.length === 0) return false;
                            return dates.every(d => {
                              if (normalizedStart && d < normalizedStart) return false;
                              if (normalizedEnd && d > normalizedEnd) return false;
                              return true;
                            });
                          });
                          setFilteredData(filterAndSort(filtered, searchInput, chips));
                        }}
                        views={['day']}
                        sx={{ '& .MuiPickersArrowSwitcher-root': { display: 'none' } }}
                      />
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>End Date</Typography>
                      {(() => { const endMonth = addMonths(calendarMonth, 1); return (
                      <DateCalendar
                        key={`e-${calendarMonth.getFullYear()}-${calendarMonth.getMonth()}`}
                        value={dateEnd && dateEnd.getMonth() === endMonth.getMonth() && dateEnd.getFullYear() === endMonth.getFullYear() ? dateEnd : null}
                        referenceDate={startOfMonth(endMonth)}
                        onChange={(newVal) => {
                          setDateEnd(newVal);
                          setPage(0);
                          const normalizedStart = dateStart ? startOfDay(dateStart) : null;
                          const normalizedEnd = newVal ? endOfDay(newVal) : null;
                          const filtered = data.filter(r => {
                            const dates = [r.complainReportingDate, r.startDate].filter(Boolean).map(d => startOfDay(new Date(d)));
                            if (dates.length === 0) return false;
                            return dates.every(d => {
                              if (normalizedStart && d < normalizedStart) return false;
                              if (normalizedEnd && d > normalizedEnd) return false;
                              return true;
                            });
                          });
                          setFilteredData(filterAndSort(filtered, searchInput, chips));
                          closeCalendars();
                        }}
                        views={['day']}
                        sx={{ '& .MuiPickersArrowSwitcher-root': { display: 'none' } }}
                      />
                      ); })()}
                    </Box>
                    <IconButton
                      onClick={() => setCalendarMonth(prev => addMonths(prev, 1))}
                      sx={{ mt: 4 }}
                    >
                      <Typography sx={{ fontSize: '1.2rem' }}>&gt;</Typography>
                    </IconButton>
                  </Box>
                  </Suspense>
                </Popover>


              </LocalizationProvider>

              {/* Chips */}
              <div className="filterChips" style={{ marginTop: 8 }}>
                {chips.map((chip, idx) => (
                  <Chip
                    key={`${chip.name}-${idx}`}
                    label={chip.name}
                    onDelete={() => removeChip(idx)}
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ))}
              </div>




            </div>


            {/* Filter Panel */}
            {focusState && (
              <div className="filterPanel col-12">
                <div className="row">
                  {/* Row 1: Four fields side-by-side (on sm+ screens), stacked on mobile */}
                  <div className="row">
                    <div className="col-12 col-sm-3">
                      <TextField
                        label="Report Number"
                        value={searchParameter.reportNo}
                        onChange={handleParamChange("reportNo")}
                        fullWidth
                        size="small"
                        variant="standard"
                        InputLabelProps={{
                          // Label font = GEG Body Copy
                          sx: {
                            fontFamily: '"GEG Body Copy","Segoe UI",Arial,sans-serif',
                            fontSize: '0.95rem'
                          }
                        }}
                      />
                    </div>

                    <div className="col-12 col-sm-3">
                      <TextField
                        label="Supplier Name"
                        value={searchParameter.supplierName}
                        onChange={handleParamChange("supplierName")}
                        fullWidth
                        size="small"
                        variant="standard"
                        InputLabelProps={{
                          sx: {
                            fontFamily: '"GEG Body Copy","Segoe UI",Arial,sans-serif',
                            fontSize: '0.95rem'
                          }
                        }}
                      />
                    </div>

                    <div className="col-12 col-sm-3">
                      <TextField
                        label="Part Number"
                        value={searchParameter.partNo}
                        onChange={handleParamChange("partNo")}
                        fullWidth
                        size="small"
                        variant="standard"
                        InputLabelProps={{
                          sx: {
                            fontFamily: '"GEG Body Copy","Segoe UI",Arial,sans-serif',
                            fontSize: '0.95rem'
                          }
                        }}
                      />
                    </div>


                    <div className="col-12 col-sm-3">
                      <FormControl fullWidth variant="standard" size="small">
                        <InputLabel
                          id="division-label"
                          shrink
                          sx={{
                            color: '#FFFFFF',                           // ← make label white
                            fontFamily: '"GEG Body Copy","Segoe UI",Arial,sans-serif',
                            fontSize: '0.95rem',
                            // Optional: ensure white on hover/focus too
                            '&.Mui-focused': { color: '#FFFFFF' },
                            '&.MuiInputLabel-root': { color: '#FFFFFF' }
                          }}
                        >
                          Division
                        </InputLabel>

                        <Select
                          labelId="division-label"
                          value={searchParameter.division || ""}
                          onChange={(e) =>
                            setSearchParameter((prev) => ({
                              ...prev,
                              division: String(e.target.value || "")
                            }))
                          }
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              // placeholder text inside the field
                              return <span style={{ opacity: 0.7, color: '#000000de' }}>Division</span>;
                            }
                            return selected;
                          }}
                          sx={{
                            "& .MuiSelect-select": {
                              fontFamily: '"GEG Body Copy","Segoe UI",Arial,sans-serif',
                              fontSize: '0.95rem',

                            },
                            "& .MuiInputBase-root": { alignItems: "center" },
                            "& .MuiInput-underline:before": { borderBottom: "1px solid #F1F1ED" },
                            "& .MuiInput-underline:hover:before": { borderBottom: "1px solid #F1F1ED" },
                            "& .MuiInput-underline:after": { borderBottom: "1px solid #F1F1ED" },
                          }}
                          MenuProps={{ disableAutoFocusItem: true }}
                        >
                          {Divisions.map((divCode) => (
                            <MenuItem key={divCode} value={divCode}>
                              {divCode}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>


                  </div>

                  {/* Row 2: Emails + Overdue */}
                  <div className="row mt-2">
                    <div className="col-12 col-sm-3">
                      <Autocomplete
                        freeSolo
                        options={mapEmails(originatorEmailsList)}
                        inputValue={searchParameter.originatorEmailID}
                        onInputChange={handleEmailTyping("originatorEmailID")}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Originator"
                            size="small"
                            variant="standard"
                            InputLabelProps={{
                              sx: {
                                fontFamily: '"GEG Body Copy","Segoe UI",Arial,sans-serif',
                                fontSize: '0.95rem'
                              }
                            }}
                            fullWidth
                          />
                        )}
                      />
                    </div>

                    <div className="col-12 col-sm-3">
                      <Autocomplete
                        freeSolo
                        options={mapEmails(coordinatorEmailsList)}
                        inputValue={searchParameter.coordinatorEmailID}
                        onInputChange={handleEmailTyping("coordinatorEmailID")}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Co-ordinator"
                            size="small"
                            variant="standard"
                            InputLabelProps={{
                              sx: {
                                fontFamily: '"GEG Body Copy","Segoe UI",Arial,sans-serif',
                                fontSize: '0.95rem'
                              }
                            }}
                            fullWidth
                          />
                        )}
                      />
                    </div>

                    <div className="col-12 col-sm-3">
                      <Autocomplete
                        freeSolo
                        options={mapEmails(responsibleEmailsList)}
                        inputValue={searchParameter.responsibleEmailID}
                        onInputChange={handleEmailTyping("responsibleEmailID")}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Responsible Person"
                            size="small"
                            variant="standard"
                            InputLabelProps={{
                              sx: {
                                fontFamily: '"GEG Body Copy","Segoe UI",Arial,sans-serif',
                                fontSize: '0.95rem'
                              }
                            }}
                            fullWidth
                          />
                        )}
                      />
                    </div>


                    <div className="col-12 col-sm-3">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={searchParameter.isOverdue}
                            onChange={(e) =>
                              setSearchParameter((prev) => ({
                                ...prev,
                                isOverdue: e.target.checked
                              }))
                            }
                            color="primary"
                            size="small"
                          />
                        }
                        label="Report with Overdue Form(s)"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontFamily: '"GEG Body Copy","Segoe UI",Arial,sans-serif',
                            fontSize: "0.95rem",
                            // opacity: 0.7, 
                            // color: '#000000de'  // ✅ same color as Responsible Person label
                          }
                        }}
                      />
                    </div>



                    <div className="row mt-2">
                      <div className="col-12">
                        <TextField
                          label="Problem Statement"
                          value={searchParameter.problemStatement}
                          onChange={handleParamChange("problemStatement")}
                          fullWidth
                          size="small"
                          variant="standard"
                          multiline
                          maxRows={3}
                          // 👇 Same as your "Responsible Person" TextField label styling
                          InputLabelProps={{
                            sx: {
                              fontFamily: '"GEG Body Copy","Segoe UI",Arial,sans-serif',
                              fontSize: '0.95rem'

                            }
                          }}
                        />
                      </div>
                    </div>

                  </div>





                  {/* Actions */}
                  <div className="row mt-3">
                    <div className="col-12 text-center actionsButtons">
                      <Button type="reset" onClick={handleReset} variant="outlined" className="btn-reset">
                        Reset
                      </Button>
                      <Button
                        type="button"
                        className="active btn-search"
                        variant="contained"
                        onClick={handleApply}
                        sx={{ ml: 1 }}
                      >
                        Search
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}



            {/* Table */}
            {/* <Paper className="reportsTable" style={{ marginTop: "20px" }}>
              <TableContainer>
                <Table className="mat-elevation-z8">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={
                            selected.length > 0 && selected.length < data.length
                          }
                          checked={data.length > 0 && selected.length === data.length}
                          onChange={handleSelectAllClick}
                          inputProps={{ "aria-label": "select all rows" }}
                        />
                      </TableCell>
                      <TableCell>8D Report No.</TableCell>
                      <TableCell>Complaint Reporting Date</TableCell>
                      <TableCell>Source of Complain</TableCell>
                      <TableCell>Supplier Name</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell className="actionsBtn">Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {data
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => {
                        const isItemSelected = isSelected(row.eightDInfoId);
                        return (
                          <TableRow
                            key={row.eightDInfoId}
                            hover
                            onClick={() => handleRowSelect(row.eightDInfoId)}
                            selected={isItemSelected}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isItemSelected}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => handleRowSelect(row.eightDInfoId)}
                                inputProps={{
                                  "aria-label": `select row ${row.eightDInfoId}`
                                }}
                              />
                            </TableCell>
                            <TableCell>{row.reportNo}</TableCell>
                            <TableCell>
                              {new Date(row.complainReportingDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell>{row.sourceOfComplain}</TableCell>
                            <TableCell>{row.supplierName}</TableCell>
                            <TableCell>
                              {new Date(row.startDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              {new Date(row.endDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="Fstatus">
                              <span>
                                <i>Status :</i> {row.status}
                              </span>
                            </TableCell>
                            <TableCell>
                              <IconButton
                                aria-label="view"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onView(row);
                                }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                              <IconButton
                                aria-label="edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(row);
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                aria-label="delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(row);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 100]}
                component="div"
                count={data.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />

              <div className="pageCount">
                <i>
                  Total Number of Pages{" "}
                  <span>{Math.ceil(data.length / rowsPerPage)}</span>
                </i>
              </div>
            </Paper> */}



            {<Paper className="reportsTable" style={{ marginTop: "20px" }}>
              <TableContainer className="reportsTableContainer">
                <Table className="mat-elevation-z8 reportsTableCore">
                  <TableHead>
                    <TableRow className="tableHeaderRow">
                      <TableCell className="tableHeaderCell checkboxHeader">
                        <Checkbox
                          indeterminate={selected.length > 0 && selected.length < displayData.length}
                          checked={displayData.length > 0 && selected.length === displayData.length}
                          onChange={handleSelectAllClick}
                        />
                      </TableCell>
                      <TableCell className="tableHeaderCell">8D Report No.</TableCell>
                      <TableCell className="tableHeaderCell">Complaint Reporting Date</TableCell>
                      <TableCell className="tableHeaderCell">Problem Statement</TableCell>
                      <TableCell className="tableHeaderCell">Part No.</TableCell>
                      <TableCell className="tableHeaderCell">Part Description</TableCell>
                      <TableCell className="tableHeaderCell">Source of Complain</TableCell>
                      <TableCell className="tableHeaderCell">Supplier Name</TableCell>
                      <TableCell className="tableHeaderCell">Start Date</TableCell>
                      <TableCell className="tableHeaderCell">End Date</TableCell>
                      <TableCell className="tableHeaderCell">Status</TableCell>
                      <TableCell className="tableHeaderCell actionsHeader">Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loading && displayData.length === 0 ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={`skel-${i}`}>
                          {Array.from({ length: 12 }).map((_, j) => (
                            <TableCell key={j}><Skeleton variant="text" /></TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : displayData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => {
                        const isItemSelected = isSelected(row.eightDInfoId);
                        return (
                          <TableRow
                            key={row.eightDInfoId}
                            hover
                            onClick={() => handleRowSelect(row.eightDInfoId)}
                            selected={isItemSelected}
                            className={`tableDataRow ${setClass(row.status)} ${isItemSelected ? "rowSelected" : ""}`}

                          >
                            <TableCell padding="checkbox" className="dataCell narrowCell">
                              <Checkbox
                                checked={isItemSelected}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => handleRowSelect(row.eightDInfoId)}
                                inputProps={{ "aria-label": `select row ${row.eightDInfoId}` }}
                                sx={{ padding: 0 }}
                              />
                            </TableCell>

                            <TableCell className="dataCell narrowCell">{row.reportNo}</TableCell>
                            <TableCell className="dataCell">
                              {row.complainReportingDate ? new Date(row.complainReportingDate).toLocaleDateString() : ''}
                            </TableCell>
                            <TableCell className="dataCell ellipsisCell">{row.problemStatement}</TableCell>
                            <TableCell className="dataCell narrowCell">{row.partNo}</TableCell>
                            <TableCell className="dataCell ellipsisCell">{row.partDescription}</TableCell>
                            <TableCell className="dataCell">{row.sourceOfComplain}</TableCell>
                            <TableCell className="dataCell">{row.supplierName}</TableCell>
                            <TableCell className="dataCell">{row.startDate ? new Date(row.startDate).toLocaleDateString() : ''}</TableCell>
                            <TableCell className="dataCell">{row.endDate ? new Date(row.endDate).toLocaleDateString() : ''}</TableCell>

                            <TableCell className="dataCell Fstatus">
                              <span className="statusText">
                                {/* <i>Status: </i> */}
                                <a className="status-link">
                                  {row.status}
                                </a>
                              </span>
                            </TableCell>

                            {/*<TableCell className="dataCell actionsCell">
                  <IconButton
                    aria-label="view"
                    onClick={(e) => {
                      e.stopPropagation();
                      onView(row);
                    }}
                    className="actionBtn"
                    size="small"
                  >
                    <VisibilityIcon  sx={{ color: "#00000061" }} />
                  </IconButton>
                  {/* <IconButton
                    aria-label="edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(row);
                    }}
                    className="actionBtn"
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    aria-label="delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(row);
                    }}
                    className="actionBtn"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton> 
                </TableCell>*/}
                            <TableCell className="dataCell actionsCell">
                              {actualMode === "reports" ? (
                                // View button for reports page
                                <IconButton
                                  aria-label="view"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onView(row);
                                  }}
                                  className="actionBtn"
                                  size="small"
                                >
                                  <VisibilityIcon sx={{ color: "#00000061" }} />
                                </IconButton>
                              ) : (
                                <>
                                  {/* Edit button for 8D forms page */}
                                  <IconButton
                                    aria-label="edit"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onEdit(row);
                                    }}
                                    className="actionBtn"
                                    size="small"
                                  >
                                    <img src={EditIcon} alt="edit" style={{ width: 24, height: 24 }} />
                                  </IconButton>

                                  {/* Delete button for 8D forms page */}
                                  <IconButton
                                    aria-label="delete"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onDelete(row);
                                    }}
                                    className="actionBtn"
                                    size="small"
                                  >
                                    <img src={DeleteIcon} alt="delete" style={{ width: 24, height: 24 }} />
                                  </IconButton>
                                </>
                              )}
                            </TableCell>

                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* <TablePagination
    rowsPerPageOptions={[5, 10, 25, 100]}
    component="div"
    count={data.length}
    rowsPerPage={rowsPerPage}
    page={page}
    onPageChange={handleChangePage}
    onRowsPerPageChange={handleChangeRowsPerPage}
    className="tablePagination"
  /> */}


              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 100]}
                component="div"
                count={displayData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                className="tablePagination"
                labelRowsPerPage="Items per page"
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    fontFamily: '"GEG Body Copy","Segoe UI", Arial, sans-serif',
                  },
                  '& .MuiTablePagination-selectLabel': {
                    fontFamily: '"GEG Body Copy","Segoe UI", Arial, sans-serif',
                  },
                  '& .MuiTablePagination-select': {
                    fontFamily: '"GEG Body Copy","Segoe UI", Arial, sans-serif',
                  },
                  '& .MuiTablePagination-displayedRows': {
                    display: 'none',
                  },
                }}
              />

              <div className="pageCount">
                <i>
                  Total Number of Pages <span>{Math.ceil(displayData.length / rowsPerPage)}</span>
                </i>
              </div>
            </Paper>}



            {/* <div className="reportsTable" style={{ marginTop: '20px' }}>
      <div className="reportsTableContainer">
        <table className="mat-elevation-z8 reportsTableCore">
          <thead>
            <tr className="tableHeaderRow">
              {/* Checkbox header cell }
              <th className="tableHeaderCell checkboxHeaderCell">
                <input
                  type="checkbox"
                  aria-label="select all rows"
                  checked={allOnPageSelected}
                  ref={el => {
                    if (el) el.indeterminate = someOnPageSelected;
                  }}
                  onChange={onToggleAll}
                />
              </th>

              <th className="tableHeaderCell">8D Report No.</th>
              <th className="tableHeaderCell">Complaint Reporting Date</th>
              <th className="tableHeaderCell">Problem Statement</th>
              <th className="tableHeaderCell">Part No.</th>
              <th className="tableHeaderCell">Part Description</th>
              <th className="tableHeaderCell">Source of Complain</th>
              <th className="tableHeaderCell">Supplier Name</th>
              <th className="tableHeaderCell">Start Date</th>
              <th className="tableHeaderCell">End Date</th>
              <th className="tableHeaderCell">Status</th>
              <th className="tableHeaderCell actionsHeader">Actions</th>
            </tr>
          </thead>

          <tbody>
            {pageRows.map(row => {
              const isSelected = selectedIds.includes(row.eightDInfoId);
              return (
                <tr
                  key={row.eightDInfoId}
                  className={`tableDataRow ${isSelected ? 'rowSelected' : ''}`}
                  onClick={() => onToggleRow(row.eightDInfoId)}
                >
                  {/* Checkbox body cell }
                  <td className="dataCell checkboxCell" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`select row ${row.eightDInfoId}`}
                      checked={isSelected}
                      onChange={() => onToggleRow(row.eightDInfoId)}
                    />
                  </td>

                  <td className="dataCell narrowCell">{row.reportNo}</td>
                  <td className="dataCell">{fmt(row.complainReportingDate)}</td>
                  <td className="dataCell ellipsisCell">{row.problemStatement}</td>
                  <td className="dataCell narrowCell">{row.partNo}</td>
                  <td className="dataCell ellipsisCell">{row.partDescription}</td>
                  <td className="dataCell">{row.sourceOfComplain}</td>
                  <td className="dataCell">{row.supplierName}</td>
                  <td className="dataCell">{fmt(row.startDate)}</td>
                  <td className="dataCell">{fmt(row.endDate)}</td>

                  <td className="dataCell Fstatus">
                    <span className="statusText">
                      <a>{row.status}</a>
                    </span>
                  </td>

                  <td className="dataCell actionsCell" onClick={e => e.stopPropagation()}>
                    <button
                      className="actionBtn"
                      aria-label="view"
                      onClick={() => onView(row)}
                      title="View"
                    >
                      
                    </button>
                    {/* add edit/delete if needed }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Simple pagination bar }
      <div className="tablePagination">
        <label style={{ marginRight: 8 }}>Rows per page:</label>
        <select
          value={rowsPerPage}
          onChange={e => onRowsPerPageChange(parseInt(e.target.value, 10))}
          style={{ marginRight: 16 }}
        >
          {[5, 10, 25, 100].map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>

        <span style={{ marginRight: 16 }}>
          {start + 1}-{Math.min(end, data.length)} of {data.length}
        </span>

        <button
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page === 0}
          aria-label="previous page"
          style={{ marginRight: 8 }}
        >
          ‹
        </button>
        <button
          onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          aria-label="next page"
        >
          ›
        </button>
      </div>

      <div className="pageCount">
        <i>
          Total Number of Pages <span>{totalPages}</span>
        </i>
      </div>
    </div> */}




          </div>
        </form>

        <DeleteConfirmation
          open={deleteDialogOpen}
          onClose={handleDeleteConfirm}
          msg="Are you sure you want to delete this record?"
        />

        {deleteAlert && (
          <Box sx={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, minWidth: '300px' }}>
            <Alert
              severity={deleteAlert === 'Record deleted successfully' ? 'success' : 'error'}
              onClose={() => setDeleteAlert(null)}
            >
              {deleteAlert}
            </Alert>
          </Box>
        )}
      </div>
    </Header>
  );
}

export default ReportsPage;
